import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Shirt,
  Plus,
  Calendar,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  Timer,
  Droplets,
  Package,
  Settings,
  Zap,
  Trash,
  Bot,
  RefreshCw,
  CalendarDays,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { escapeDeep } from '../lib/escapeHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LaundryManagement = () => {
  const { user } = useAuth();
  const [laundrySessions, setLaundrySessions] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('daily');
  
  // Admin assignment controls
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time_slot: '07:00-09:00',
    assigned_to: '',
    assigned_type: 'resident',
    assigned_name: '',
    loads_planned: 1,
    supplies_used: [],
    notes: ''
  });

  const timeSlots = [
    { value: '07:00-09:00', label: '7:00 AM - 9:00 AM' },
    { value: '09:00-11:00', label: '9:00 AM - 11:00 AM' },
    { value: '11:00-13:00', label: '11:00 AM - 1:00 PM' },
    { value: '13:00-15:00', label: '1:00 PM - 3:00 PM' },
    { value: '15:00-17:00', label: '3:00 PM - 5:00 PM' },
    { value: '17:00-19:00', label: '5:00 PM - 7:00 PM' },
    { value: '19:00-21:00', label: '7:00 PM - 9:00 PM' }
  ];

  const commonSupplies = [
    'Laundry Detergent',
    'Fabric Softener',
    'Bleach',
    'Stain Remover',
    'Dryer Sheets'
  ];

  useEffect(() => {
    if (activeTab === 'daily') {
      fetchLaundrySessions();
    } else {
      fetchWeeklyLaundrySessions();
    }
    fetchResidents();
  }, [selectedDate, activeTab]);

  const fetchLaundrySessions = async () => {
    try {
      const response = await axios.get(`${API}/laundry?date=${selectedDate}`);
      setLaundrySessions(response.data);
    } catch (error) {
      console.error('Error fetching laundry sessions:', error);
      toast.error('Failed to load laundry sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchResidents = async () => {
    try {
      const response = await axios.get(`${API}/residents?status=current`);
      setResidents(response.data);
    } catch (error) {
      console.error('Error fetching residents:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let assignedName = '';
      if (formData.assigned_type === 'resident') {
        const resident = residents.find(r => r.id === formData.assigned_to);
        assignedName = resident ? `${resident.first_name} ${resident.last_name}` : 'Unknown Resident';
      } else {
        assignedName = 'Staff Member';
      }

      const laundryData = {
        ...formData,
        assigned_name: assignedName
      };
      
      await axios.post(`${API}/laundry`, laundryData);
      toast.success('Laundry session scheduled successfully');
      resetForm();
      fetchLaundrySessions();
    } catch (error) {
      console.error('Error creating laundry session:', error);
      toast.error('Failed to schedule laundry session');
    }
  };

  const handleCompleteSession = async (sessionId, completionData) => {
    try {
      await axios.put(`${API}/laundry/${sessionId}/complete`, completionData);
      toast.success('Laundry session completed');
      fetchLaundrySessions();
    } catch (error) {
      console.error('Error completing laundry session:', error);
      toast.error('Failed to complete laundry session');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time_slot: '07:00-09:00',
      assigned_to: '',
      assigned_type: 'resident',
      assigned_name: '',
      loads_planned: 1,
      supplies_used: [],
      notes: ''
    });
    setShowAddDialog(false);
  };
  const resetCompleteForm = () => {
    setCompleteData({
      completed_by_resident: '',
      completed_by_resident_id: '',
      notes: ''
    });
  };

  // Print Functions
  const printDailyLaundrySchedule = () => {
    const printDate = selectedDate || new Date().toISOString().split('T')[0];
    const dailySessions = escapeDeep(laundrySessions.filter(session => session.date === printDate));
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Laundry Schedule - ${new Date(printDate).toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.5; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
          .schedule-item { border: 1px solid #ddd; margin: 10px 0; padding: 15px; page-break-inside: avoid; }
          .time-slot { font-size: 18px; font-weight: bold; color: #2563eb; }
          .assigned-to { font-size: 16px; margin: 5px 0; }
          .details { margin: 10px 0; }
          .status { padding: 3px 8px; border-radius: 4px; font-size: 12px; }
          .status.pending { background-color: #fef3c7; color: #92400e; }
          .status.completed { background-color: #dcfce7; color: #166534; }
          .status.in_progress { background-color: #dbeafe; color: #1d4ed8; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🧺 Daily Laundry Schedule</h1>
          <h2>${new Date(printDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h2>
        </div>
        
        ${dailySessions.length === 0 ? 
          '<div style="text-align: center; margin: 50px 0; color: #666;"><h3>No laundry sessions scheduled for this date</h3></div>' :
          dailySessions
            .sort((a, b) => {
              const timeOrder = ["07:00-09:00", "09:00-11:00", "11:00-13:00", "13:00-15:00", "15:00-17:00", "17:00-19:00", "19:00-21:00"];
              return timeOrder.indexOf(a.time_slot) - timeOrder.indexOf(b.time_slot);
            })
            .map(session => {
              const timeSlotLabel = timeSlots.find(t => t.value === session.time_slot)?.label || session.time_slot;
              return `
                <div class="schedule-item">
                  <div class="time-slot">${timeSlotLabel}</div>
                  <div class="assigned-to"><strong>Assigned to:</strong> ${session.assigned_name}</div>
                  <div class="details">
                    <strong>Loads Planned:</strong> ${session.loads_planned} | 
                    <strong>Loads Completed:</strong> ${session.loads_completed}
                  </div>
                  ${session.supplies_used.length > 0 ? 
                    `<div class="details"><strong>Supplies:</strong> ${session.supplies_used.join(', ')}</div>` : ''
                  }
                  ${session.notes ? 
                    `<div class="details"><strong>Notes:</strong> ${session.notes}</div>` : ''
                  }
                  <div class="details">
                    <span class="status ${session.status}">${session.status.toUpperCase().replace('_', ' ')}</span>
                  </div>
                </div>
              `;
            }).join('')
        }
        
        <div class="footer">
          <p>Shelter Care Connect - Laundry Management System</p>
          <p>Printed on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const printWeeklyLaundrySchedule = () => {
    // Group sessions by week
    const weeklyGroups = {};
    escapeDeep(laundrySessions).forEach(session => {
      const sessionDate = new Date(session.date);
      const weekStart = new Date(sessionDate);
      weekStart.setDate(sessionDate.getDate() - sessionDate.getDay() + 1); // Monday
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyGroups[weekKey]) {
        weeklyGroups[weekKey] = [];
      }
      weeklyGroups[weekKey].push(session);
    });

    const sortedWeeks = Object.keys(weeklyGroups).sort();
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Laundry Assignments</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
          .week-section { margin: 20px 0; page-break-before: auto; }
          .week-header { background-color: #f3f4f6; padding: 10px; font-size: 18px; font-weight: bold; }
          .resident-group { border: 1px solid #ddd; margin: 10px 0; padding: 10px; }
          .resident-name { font-size: 16px; font-weight: bold; color: #1f2937; }
          .assignment { margin: 5px 0; padding: 5px; background-color: #f9fafb; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          @media print {
            body { margin: 0; }
            .week-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🧺 Weekly Laundry Assignments</h1>
          <h2>Auto-Assigned Schedule Overview</h2>
        </div>
        
        ${sortedWeeks.map(weekStart => {
          const weekSessions = weeklyGroups[weekStart];
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 5); // Saturday
          
          // Group by resident
          const residentGroups = {};
          weekSessions.forEach(session => {
            if (!residentGroups[session.assigned_name]) {
              residentGroups[session.assigned_name] = [];
            }
            residentGroups[session.assigned_name].push(session);
          });

          return `
            <div class="week-section">
              <div class="week-header">
                Week of ${new Date(weekStart).toLocaleDateString()} - ${weekEnd.toLocaleDateString()}
                (${Object.keys(residentGroups).length} residents, ${weekSessions.length} assignments)
              </div>
              
              ${Object.entries(residentGroups).map(([residentName, sessions]) => `
                <div class="resident-group">
                  <div class="resident-name">${residentName} - ${sessions.length} time slots</div>
                  ${sessions
                    .sort((a, b) => {
                      const dateCompare = new Date(a.date) - new Date(b.date);
                      if (dateCompare !== 0) return dateCompare;
                      const timeOrder = ["07:00-09:00", "09:00-11:00", "11:00-13:00", "13:00-15:00", "15:00-17:00", "17:00-19:00", "19:00-21:00"];
                      return timeOrder.indexOf(a.time_slot) - timeOrder.indexOf(b.time_slot);
                    })
                    .map(session => `
                      <div class="assignment">
                        <strong>${new Date(session.date).toLocaleDateString('en-US', { weekday: 'short' })}</strong> - 
                        ${session.time_slot} 
                        <span style="color: ${session.status === 'completed' ? 'green' : session.status === 'pending' ? 'orange' : 'blue'};">
                          (${session.status.replace('_', ' ')})
                        </span>
                      </div>
                    `).join('')
                  }
                </div>
              `).join('')}
            </div>
          `;
        }).join('')}
        
        <div class="footer">
          <p>Shelter Care Connect - Laundry Management System</p>
          <p>Printed on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  // Admin assignment functions
  const handleClearAllAssignments = () => {
    setShowClearDialog(true);
  };

  const confirmClearAllAssignments = async () => {
    try {
      setAssignmentLoading(true);
      const response = await axios.post(`${API}/assignments/clear-all`);
      toast.success(`Successfully cleared ${response.data.chores_cleared} chores and ${response.data.laundry_sessions_cleared} laundry sessions`);
      setShowClearDialog(false);
      fetchLaundrySessions(); // Refresh laundry display
    } catch (error) {
      console.error('Error clearing assignments:', error);
      if (error.response?.status === 403) {
        toast.error('Only administrators can clear all assignments');
      } else {
        toast.error('Failed to clear assignments');
      }
      setShowClearDialog(false);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const cancelClearAllAssignments = () => {
    setShowClearDialog(false);
  };

  const handleTriggerDailyAssignment = async () => {
    if (!window.confirm(`📅 Trigger daily auto-assignment for ${selectedDate}? This will create new laundry slots for the selected date.`)) {
      return;
    }
    
    try {
      setAssignmentLoading(true);
      const response = await axios.post(`${API}/assignments/daily-auto-assign`, {
        date: selectedDate
      });
      toast.success(`📅 Daily assignments created! ${response.data.chores_created} chores + ${response.data.laundry_slots_created} laundry slots for ${selectedDate}. Triggered by: ${response.data.triggered_by}`);
      fetchLaundrySessions(); // Refresh laundry display
    } catch (error) {
      console.error('Error triggering daily assignment:', error);
      if (error.response?.status === 403) {
        toast.error('Staff or admin privileges required');
      } else {
        toast.error('Failed to trigger daily assignments');
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleTriggerWeeklyAssignment = async () => {
    if (!window.confirm('🤖 Trigger weekly auto-assignment now? This will create new chore and laundry assignments for the upcoming week.')) {
      return;
    }

    try {
      setAssignmentLoading(true);
      const response = await axios.post(`${API}/assignments/weekly-auto-assign`);
      toast.success(`Weekly auto-assignments completed successfully! Triggered by: ${response.data.triggered_by}`);
      fetchLaundrySessions(); // Refresh laundry display
    } catch (error) {
      console.error('Error triggering weekly assignment:', error);
      if (error.response?.status === 403) {
        toast.error('Staff or admin privileges required');
      } else {
        toast.error('Failed to trigger weekly assignments');
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  // Fetch weekly laundry sessions
  const fetchWeeklyLaundrySessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/laundry`);
      setLaundrySessions(response.data);
    } catch (error) {
      console.error('Error fetching weekly laundry sessions:', error);
      toast.error('Failed to load weekly laundry sessions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return CheckCircle;
      case 'in_progress':
        return Timer;
      default:
        return Clock;
    }
  };

  const getTimeSlotIcon = (timeSlot) => {
    switch (timeSlot) {
      case 'morning':
        return '🌅';
      case 'afternoon':
        return '☀️';
      case 'evening':
        return '🌆';
      default:
        return '⏰';
    }
  };

  const handleSupplyToggle = (supply) => {
    const supplies = formData.supplies_used.includes(supply)
      ? formData.supplies_used.filter(s => s !== supply)
      : [...formData.supplies_used, supply];
    
    setFormData({
      ...formData,
      supplies_used: supplies
    });
  };

  const todaysSessions = laundrySessions.filter(session => 
    session.date === new Date().toISOString().split('T')[0]
  );
  const completedToday = todaysSessions.filter(session => session.status === 'completed').length;
  const totalLoadsToday = todaysSessions.reduce((sum, session) => sum + session.loads_completed, 0);

  // Render daily laundry sessions list
  const renderLaundrySessionsList = () => {
    return loading ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="card-shadow">
            <CardContent className="p-6">
              <div className="skeleton h-32 w-full rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    ) : laundrySessions.length === 0 ? (
      <Card className="card-shadow">
        <CardContent className="p-12 text-center">
          <Shirt className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No laundry sessions scheduled</h3>
          <p className="text-gray-600 mb-4">No laundry sessions have been scheduled for {selectedDate}.</p>
          <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-2" />
            Schedule First Session
          </Button>
        </CardContent>
      </Card>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {laundrySessions
          .sort((a, b) => {
            // Sort by time slot chronologically
            const timeSlotOrder = [
              "07:00-09:00", "09:00-11:00", "11:00-13:00", 
              "13:00-15:00", "15:00-17:00", "17:00-19:00", "19:00-21:00"
            ];
            return timeSlotOrder.indexOf(a.time_slot) - timeSlotOrder.indexOf(b.time_slot);
          })
          .map((session) => {
          const StatusIcon = getStatusIcon(session.status);
          const timeSlotLabel = timeSlots.find(t => t.value === session.time_slot)?.label || session.time_slot;
          
          return (
            <Card key={session.id} className="card-shadow hover:card-shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getTimeSlotIcon(session.time_slot)}</span>
                    <div>
                      <CardTitle className="text-lg capitalize">
                        {session.time_slot} Session
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {timeSlotLabel}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className={`px-3 py-1 ${getStatusColor(session.status)}`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {session.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Assignment Info */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  <span>Assigned to: <strong>{session.assigned_name}</strong></span>
                </div>

                {/* Date */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(session.date).toLocaleDateString()}</span>
                </div>

                {/* Loads Info */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Droplets className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-900">Loads</span>
                  </div>
                  <span className="text-blue-800 font-bold">
                    {session.loads_completed} / {session.loads_planned}
                  </span>
                </div>

                {/* Supplies Used */}
                {session.supplies_used.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                      <Package className="h-4 w-4" />
                      <span>Supplies Used</span>
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {session.supplies_used.map((supply, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {supply}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Issues */}
                {session.issues && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-900">Issues Reported</p>
                        <p className="text-sm text-red-700">{session.issues}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {session.notes && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{session.notes}</p>
                  </div>
                )}

                {/* Completion Info */}
                {session.status === 'completed' && session.completed_at && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Completed:</strong> {new Date(session.completed_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Actions */}
                {session.status === 'pending' && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => handleCompleteSession(session.id, {
                        loads_completed: session.loads_planned,
                        notes: 'Session completed successfully'
                      })}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Completed
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // Render weekly assignments view
  const renderWeeklyAssignments = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    // Group sessions by week
    const weeklyGroups = {};
    laundrySessions.forEach(session => {
      const sessionDate = new Date(session.date);
      const weekStart = new Date(sessionDate);
      weekStart.setDate(sessionDate.getDate() - sessionDate.getDay() + 1); // Monday
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyGroups[weekKey]) {
        weeklyGroups[weekKey] = [];
      }
      weeklyGroups[weekKey].push(session);
    });

    const sortedWeeks = Object.keys(weeklyGroups).sort();

    if (sortedWeeks.length === 0) {
      return (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <CalendarDays className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No weekly assignments found</h3>
            <p className="text-gray-600 mb-4">No auto-assigned laundry sessions have been created yet.</p>
            {user?.role === 'admin' && (
              <Button onClick={() => setShowAdminDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Bot className="h-4 w-4 mr-2" />
                Trigger Weekly Assignment
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-8">
        {sortedWeeks.map(weekStart => {
          const weekSessions = weeklyGroups[weekStart];
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 5); // Saturday
          
          // Group by resident
          const residentGroups = {};
          weekSessions.forEach(session => {
            if (!residentGroups[session.assigned_name]) {
              residentGroups[session.assigned_name] = [];
            }
            residentGroups[session.assigned_name].push(session);
          });

          return (
            <Card key={weekStart} className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CalendarDays className="h-5 w-5 text-indigo-600" />
                  <span>Week of {new Date(weekStart).toLocaleDateString()} - {weekEnd.toLocaleDateString()}</span>
                </CardTitle>
                <CardDescription>
                  {Object.keys(residentGroups).length} residents with {weekSessions.length} total assignments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(residentGroups).map(([residentName, sessions]) => (
                    <div key={residentName} className="p-4 border rounded-lg bg-gray-50">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>{residentName}</span>
                        <Badge variant="outline" className="ml-auto">
                          {sessions.length} slots
                        </Badge>
                      </h4>
                      <div className="space-y-2">
                        {sessions
                          .sort((a, b) => {
                            // Sort by date first, then by time slot
                            const dateCompare = new Date(a.date) - new Date(b.date);
                            if (dateCompare !== 0) return dateCompare;
                            
                            // Sort by time slot chronologically
                            const timeSlotOrder = [
                              "07:00-09:00", "09:00-11:00", "11:00-13:00", 
                              "13:00-15:00", "15:00-17:00", "17:00-19:00", "19:00-21:00"
                            ];
                            return timeSlotOrder.indexOf(a.time_slot) - timeSlotOrder.indexOf(b.time_slot);
                          })
                          .map(session => {
                          const StatusIcon = getStatusIcon(session.status);
                          return (
                            <div key={session.id} className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm">{new Date(session.date).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                <span className="text-sm font-medium">{session.time_slot}</span>
                              </div>
                              <Badge className={`px-2 py-1 text-xs ${getStatusColor(session.status)}`}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {session.status.replace('_', ' ')}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Shirt className="h-8 w-8 text-indigo-600" />
            <span>Laundry Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Auto-assigned laundry slots (2 per resident/week) and manual scheduling</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button onClick={activeTab === 'daily' ? fetchLaundrySessions : fetchWeeklyLaundrySessions} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>

          {/* Print Buttons */}
          <Button 
            onClick={activeTab === 'daily' ? printDailyLaundrySchedule : printWeeklyLaundrySchedule} 
            variant="outline" 
            size="sm"
            className="border-green-200 text-green-700 hover:bg-green-50"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Schedule
          </Button>

          {/* Admin Assignment Controls */}
          {user?.role === 'admin' && (
            <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Controls
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center space-x-2">
                    <Bot className="h-5 w-5 text-indigo-600" />
                    <span>Assignment Management</span>
                  </DialogTitle>
                  <DialogDescription>
                    Admin controls for automated chore and laundry assignments
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-medium text-green-900 mb-2">📅 Daily Auto-Assignment</h3>
                    <p className="text-sm text-green-700 mb-3">
                      Create laundry slots and chores for the selected date ({selectedDate}). 
                      Assigns residents to available time slots and room-based chores.
                    </p>
                    <Button 
                      onClick={handleTriggerDailyAssignment}
                      disabled={assignmentLoading}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      {assignmentLoading ? 'Triggering...' : `Trigger Daily Assignment (${selectedDate})`}
                    </Button>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">🤖 Weekly Auto-Assignment</h3>
                    <p className="text-sm text-blue-700 mb-3">
                      Automatically assigns each resident 2 laundry time slots per week (Mon-Sat) and chores to rooms (3x daily). 
                      Normally runs every Sunday at 9 PM.
                    </p>
                    <Button 
                      onClick={handleTriggerWeeklyAssignment}
                      disabled={assignmentLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      {assignmentLoading ? 'Triggering...' : 'Trigger Weekly Assignment'}
                    </Button>
                  </div>

                  <div className="p-4 bg-red-50 rounded-lg">
                    <h3 className="font-medium text-red-900 mb-2">🗑️ Clear All Assignments</h3>
                    <p className="text-sm text-red-700 mb-3">
                      Remove all existing chores and laundry assignments from the system. 
                      Use with caution - this action cannot be undone.
                    </p>
                    <Button 
                      onClick={handleClearAllAssignments}
                      disabled={assignmentLoading}
                      variant="destructive"
                      className="w-full"
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      {assignmentLoading ? 'Clearing...' : 'Clear All Assignments'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Schedule Laundry</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule Laundry Session</DialogTitle>
              <DialogDescription>
                Assign a laundry session to a resident or staff member
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Date *</label>
                  <Input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Time Slot *</label>
                  <Select
                    value={formData.time_slot}
                    onValueChange={(value) => setFormData({...formData, time_slot: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot.value} value={slot.value}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Assign To</label>
                  <Select
                    value={formData.assigned_type}
                    onValueChange={(value) => setFormData({...formData, assigned_type: value, assigned_to: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resident">Resident</SelectItem>
                      <SelectItem value="staff">Staff Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    {formData.assigned_type === 'resident' ? 'Select Resident' : 'Staff Member'} *
                  </label>
                  {formData.assigned_type === 'resident' ? (
                    <Select
                      value={formData.assigned_to}
                      onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a resident..." />
                      </SelectTrigger>
                      <SelectContent>
                        {residents.map((resident) => (
                          <SelectItem key={resident.id} value={resident.id}>
                            {resident.first_name} {resident.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      required
                      value={formData.assigned_to}
                      onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                      placeholder="Enter staff member name"
                    />
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Planned Loads</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.loads_planned}
                    onChange={(e) => setFormData({...formData, loads_planned: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Supplies to Use</label>
                <div className="grid grid-cols-2 gap-2">
                  {commonSupplies.map((supply) => (
                    <label key={supply} className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.supplies_used.includes(supply)}
                        onChange={() => handleSupplyToggle(supply)}
                        className="rounded"
                      />
                      <span className="text-sm">{supply}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special instructions or notes..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                  Schedule Session
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* View Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="daily" className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Daily View</span>
          </TabsTrigger>
          <TabsTrigger value="weekly" className="flex items-center space-x-2">
            <CalendarDays className="h-4 w-4" />
            <span>Weekly Assignments</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily">
          {/* Date Selection and Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            <Card className="card-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Date Selection</p>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="mt-2"
                />
              </div>
              <Calendar className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Sessions Today</p>
                    <p className="text-2xl font-bold text-gray-900">{todaysSessions.length}</p>
                  </div>
                  <Shirt className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Completed Today</p>
                    <p className="text-2xl font-bold text-gray-900">{completedToday}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Loads Done Today</p>
                    <p className="text-2xl font-bold text-gray-900">{totalLoadsToday}</p>
                  </div>
                  <Droplets className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Laundry Sessions List */}
          {renderLaundrySessionsList()}
        </TabsContent>

        <TabsContent value="weekly">
          {/* Weekly Auto-Assignment View */}
          <div className="mb-6">
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Bot className="h-5 w-5 text-indigo-600" />
                  <span>Weekly Auto-Assignments</span>
                </CardTitle>
                <CardDescription>
                  Each resident automatically receives 2 laundry time slots per week (Monday-Saturday)
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Weekly Sessions List */}
          {renderWeeklyAssignments()}
        </TabsContent>
      </Tabs>

      {/* Clear All Assignments Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">🗑️ Clear All Assignments</DialogTitle>
            <DialogDescription>
              Are you sure you want to clear ALL chores and laundry assignments? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-start space-x-3">
              <div className="text-red-600 text-xl">⚠️</div>
              <div>
                <p className="text-sm font-medium text-red-800">This will permanently delete:</p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li>All chore assignments</li>
                  <li>All laundry sessions</li>
                  <li>All assignment history</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={cancelClearAllAssignments} disabled={assignmentLoading}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmClearAllAssignments}
              disabled={assignmentLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {assignmentLoading ? 'Clearing...' : '🗑️ Clear All Assignments'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default LaundryManagement;